import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBell, FaChevronRight, FaClipboardList, FaFileInvoiceDollar, FaPlaneDeparture } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { UserContext } from '../UserContext';
import { encryptId } from '../utils/encrypt';
import Tabs from '../components/Tabs/Tabs';
import './styles/Inbox.css';

const INBOX_REFRESH_INTERVAL_MS = 10000;

const formatDate = (value) => {
  if (!value) return 'NA';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getDisplayStatus = (request) =>
  request?.workflowStatusLabel || request?.status || 'Pending';

const getStatusCategory = (request) => {
  const status = String(
    request?.workflowStatusLabel ||
    request?.status ||
    ''
  ).toLowerCase();

  if (
    status.includes('reject') ||
    status.includes('declin') ||
    status === 'cancelled'
  ) {
    return 'Rejected';
  }

  if (
    status.includes('approv') &&
    !status.includes('pending')
  ) {
    return 'Approved';
  }

  if (
    status.includes('pending') ||
    status === 'requested'
  ) {
    return 'Pending';
  }

  return 'Pending';
};

const getBookingStatus = (request) =>
  request?.workflowStatusLabel || request?.status || 'Requested';

const getCurrentStageLabel = (currentStage) => {
  if (currentStage === 'MANAGER') return 'Pending Manager Approval';
  if (currentStage === 'ADMIN') return 'Pending Admin Approval';
  if (currentStage === 'MANAGER_REJECTED') return 'Rejected by Manager';
  if (currentStage === 'ADMIN_REJECTED') return 'Rejected by Admin';
  return currentStage || 'NA';
};

const getStatusSlug = (request) =>
  getDisplayStatus(request).toLowerCase().replace(/\s+/g, '-');

const getRequestedSection = (search) => {
  const section = new URLSearchParams(search).get('section');
  return ['expense', 'approval', 'tracking', 'notifications'].includes(section) ? section : null;
};

const normalize = (value = '') =>
  String(value)
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const hasApproverInboxAccess = (inboxData, user) => {
  const viewerRole = normalize(
    inboxData?.viewer?.role || user?.role
  );

  const hasTakeActionRequests =
    (inboxData?.takeAction?.expenseRequests || []).length > 0 ||
    (inboxData?.takeAction?.approvalRequests || []).length > 0;

  return viewerRole !== 'USER' || hasTakeActionRequests;
};

const Inbox = () => {
  const { user, token } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [inboxData, setInboxData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [activeSection, setActiveSection] = useState(() => getRequestedSection(location.search) || 'tracking');
  const [loading, setLoading] = useState(true);
  const filterRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    supportType: [],
    requestStatus: [],
    bookingStatus: [],
  });
  const [dropdownVisible, setDropdownVisible] = useState({
    supportType: false,
    requestStatus: false,
    bookingStatus: false,
  });
  const [searchHeader, setSearchHeader] = useState('batchId');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const handleSectionChange = (section) => {
    setActiveSection(section);
    navigate(`/inbox?section=${section}`, { replace: true });
  };

  const resolveActiveSection = (inboxJson, currentSection) => {
    const isApprover = hasApproverInboxAccess(inboxJson, user);
    const hasExpense = (inboxJson?.takeAction?.expenseRequests || []).length > 0;
    const hasApproval = (inboxJson?.takeAction?.approvalRequests || []).length > 0;
    const requestedSection = getRequestedSection(location.search);

    if (!isApprover && requestedSection === 'tracking') {
      return 'tracking';
    }

    if (requestedSection === 'notifications') {
      return 'notifications';
    }

    if (isApprover && requestedSection === 'expense') {
      return 'expense';
    }

    if (isApprover && requestedSection === 'approval') {
      return 'approval';
    }

    if (isApprover && currentSection === 'tracking') {
      return hasExpense ? 'expense' : hasApproval ? 'approval' : 'notifications';
    }

    if (isApprover && currentSection === 'expense' && hasExpense) {
      return currentSection;
    }

    if (isApprover && currentSection === 'approval' && hasApproval) {
      return currentSection;
    }

    if (currentSection === 'notifications') {
      return 'notifications';
    }

    if (!isApprover) {
      return 'tracking';
    }

    return hasExpense ? 'expense' : hasApproval ? 'approval' : 'notifications';
  };

  const updateActiveSection = (inboxJson) => {
    const nextSection = resolveActiveSection(inboxJson, activeSection);
    setActiveSection(nextSection);

    const requestedSection = getRequestedSection(location.search);
    if (requestedSection !== nextSection) {
      navigate(`/inbox?section=${nextSection}`, { replace: true });
    }
  };

  const fetchInboxData = async ({ showLoading = false } = {}) => {
    if (!token) return;

    if (showLoading) {
      setLoading(true);
    }

    try {
      const [inboxResponse, notificationsResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/requests/inbox`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_API_URL}/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!inboxResponse.ok) {
        throw new Error('Failed to load inbox');
      }

      const inboxJson = await inboxResponse.json();
      setInboxData(inboxJson);
      updateActiveSection(inboxJson);

      if (notificationsResponse.ok) {
        const notificationsJson = await notificationsResponse.json();
        setNotifications(notificationsJson);
      }
    } catch (error) {
      console.error('Inbox fetch failed:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!token) return;

    fetchInboxData({ showLoading: true });

    const intervalId = window.setInterval(() => {
      fetchInboxData();
    }, INBOX_REFRESH_INTERVAL_MS);

    const handleInboxRefresh = () => {
      fetchInboxData();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchInboxData();
      }
    };

    window.addEventListener('focus', handleInboxRefresh);
    window.addEventListener('routeplanner:inbox-refresh', handleInboxRefresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleInboxRefresh);
      window.removeEventListener('routeplanner:inbox-refresh', handleInboxRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, user?.role, location.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilters(false);
        setDropdownVisible({
          supportType: false,
          requestStatus: false,
          bookingStatus: false,
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const trackingRequests = inboxData?.tracking || [];
  const expenseRequests = inboxData?.takeAction?.expenseRequests || [];
  const approvalRequests = inboxData?.takeAction?.approvalRequests || [];
  const isApprover = hasApproverInboxAccess(inboxData, user);

  const currentList =
    activeSection === 'expense'
      ? expenseRequests
      : activeSection === 'approval'
        ? approvalRequests
        : activeSection === 'tracking'
          ? trackingRequests
          : [];

  const toggleDropdown = (category) => {
    setDropdownVisible((prev) => {
      const next = { ...prev, [category]: !prev[category] };
      Object.keys(next).forEach((key) => {
        if (key !== category) {
          next[key] = false;
        }
      });
      return next;
    });
  };

  const handleFilterChange = (category, value) => {
    setFilterOptions((prev) => {
      const next = { ...prev };
      if (next[category].includes(value)) {
        next[category] = next[category].filter((item) => item !== value);
      } else {
        next[category] = [...next[category], value];
      }
      return next;
    });
  };

  const clearFilters = () => {
    setFilterOptions({
      supportType: [],
      requestStatus: [],
      bookingStatus: [],
    });
    setStatusFilter('All');
    setSearchHeader('batchId');
    setSearchTerm('');
    setDropdownVisible({
      supportType: false,
      requestStatus: false,
      bookingStatus: false,
    });
    setShowFilters(false);
  };

  const markAllNotificationsRead = async () => {
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/notifications/read-all`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to mark notifications as read');
      }

      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const applyFilters = (list) => {
    let filtered = [...list];

    if (statusFilter !== 'All') {
      filtered = filtered.filter(
        (request) => getStatusCategory(request) === statusFilter
      );
    }

    if (filterOptions.supportType.length > 0) {
      filtered = filtered.filter((request) =>
        filterOptions.supportType.includes(request.supportNeeded)
      );
    }

    if (filterOptions.requestStatus.length > 0) {
      filtered = filtered.filter((request) =>
        filterOptions.requestStatus.includes(request.status || 'Requested')
      );
    }

    if (filterOptions.bookingStatus.length > 0) {
      filtered = filtered.filter((request) =>
        filterOptions.bookingStatus.includes(getBookingStatus(request))
      );
    }

    return filtered;
  };

  const applySearch = (list) => {
    if (!searchTerm.trim()) {
      return list;
    }

    return list.filter((request) => {
      let value = '';

      switch (searchHeader) {
        case 'batchId':
          value = request.batchId;
          break;
        case 'supportNeeded':
          value = request.supportNeeded;
          break;
        case 'officeLocation':
          value = request.officeLocation;
          break;
        case 'status':
          value = request.status;
          break;
        case 'bookingStatus':
          value = getBookingStatus(request);
          break;
        case 'podNumber':
          value = request.bookingDetails?.podNumber;
          break;
        case 'requesterName':
          value = request.user?.name || request.email;
          break;
        case 'center':
          value = request.center;
          break;
        case 'client':
          value = request.client;
          break;
        case 'costCenter':
          value = request.costCenter;
          break;
        case 'source':
          value = request.source || request.fromLocation;
          break;
        case 'destination':
          value = request.destination || request.toLocation;
          break;
        default:
          value = '';
      }

      return String(value || '').toLowerCase().includes(searchTerm.toLowerCase());
    });
  };

  const isFilterActive =
    statusFilter !== 'All' ||
    filterOptions.supportType.length > 0 ||
    filterOptions.requestStatus.length > 0 ||
    filterOptions.bookingStatus.length > 0;

  const isSearchActive = searchTerm.trim() !== '';

  let filteredCurrentList = [];

  if (activeSection !== 'notifications') {
    filteredCurrentList = [...currentList];

    if (isFilterActive) {
      filteredCurrentList = applyFilters(filteredCurrentList);
    }

    if (isSearchActive) {
      filteredCurrentList = applySearch(filteredCurrentList);
    }
  }

  const sectionTitle =
    activeSection === 'expense'
      ? 'Expense Requests'
      : activeSection === 'approval'
        ? 'Approval Requests'
      : activeSection === 'notifications'
        ? 'Notifications'
        : 'My Submitted Requests';

const tabData = user?.role !== 'USER'
  ? [
      {
        label: 'My Requests',
        to: '/',
        exact: true,
        state: { requestView: 'mine' },
      },
      {
        label: 'All Requests',
        to: '/',
        exact: true,
        state: { requestView: 'all' },
      },
      {
        label: 'Inbox',
        to: '/inbox',
        exact: true,
      },
    ]
  : [
      {
        label: 'My Requests',
        to: '/',
        exact: true,
        state: { requestView: 'mine' },
      },
      {
        label: 'Inbox',
        to: '/inbox',
        exact: true,
      },
    ];
  const inboxHeaderActions = activeSection !== 'notifications' ? (
    <div className="req-action-bar inbox-action-bar">
      <div className="req-search-section">
        <div className="req-search-by">
          <label>
            <strong>Search By:</strong>
            <select value={searchHeader} onChange={(e) => setSearchHeader(e.target.value)}>
              <option value="batchId">Batch ID</option>
              <option value="requesterName">Requester Name</option>
              <option value="supportNeeded">Support Needed</option>
              <option value="officeLocation">Office Location</option>
              <option value="status">Status</option>
              <option value="bookingStatus">Request Status</option>
              <option value="podNumber">POD Number</option>
              <option value="center">Center</option>
              <option value="client">Client</option>
              <option value="costCenter">Cost Center</option>
              <option value="source">Source</option>
              <option value="destination">Destination</option>
            </select>
          </label>
        </div>

        <div className="req-search-here">
          <label>
            <strong>Search Here:</strong>
            <input
              type="text"
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </label>
        </div>
      </div>
      <div className="inbox-status-filter">
        <label htmlFor="status-filter">
          <strong>Status:</strong>
        </label>

        <select
          id="status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All">All</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>
      <div className="req-filter-buttons inbox-filter-buttons" ref={filterRef}>
        {showFilters && (
          <div className="vr-filter-wrapper">
            <div className="dropdown">
              <button
                className={`vr-filter-item ${dropdownVisible.supportType ? 'active' : ''}`}
                type="button"
                onClick={() => toggleDropdown('supportType')}
              >
                Support Type
              </button>
              {dropdownVisible.supportType && (
                <div className="vr-filter-options">
                  {['Travel', 'Accommodation', 'Travel + Accommodation', 'Send Courier', 'Receive Courier'].map((opt) => (
                    <label key={opt}>
                      <input
                        type="checkbox"
                        checked={filterOptions.supportType.includes(opt)}
                        onChange={() => handleFilterChange('supportType', opt)}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="dropdown">
              <button
                className={`vr-filter-item ${dropdownVisible.requestStatus ? 'active' : ''}`}
                type="button"
                onClick={() => toggleDropdown('requestStatus')}
              >
                Status
              </button>
              {dropdownVisible.requestStatus && (
                <div className="vr-filter-options">
                  {['Requested', 'Approved', 'Booked', 'Declined', 'Cancelled'].map((opt) => (
                    <label key={opt}>
                      <input
                        type="checkbox"
                        checked={filterOptions.requestStatus.includes(opt)}
                        onChange={() => handleFilterChange('requestStatus', opt)}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="dropdown">
              <button
                className={`vr-filter-item ${dropdownVisible.bookingStatus ? 'active' : ''}`}
                type="button"
                onClick={() => toggleDropdown('bookingStatus')}
              >
                Request Status
              </button>
              {dropdownVisible.bookingStatus && (
                <div className="vr-filter-options">
                  {['Pending', 'Pending Manager Approval', 'Pending Admin Approval', 'Approved', 'Rejected', 'Cancelled'].map((opt) => (
                    <label key={opt}>
                      <input
                        type="checkbox"
                        checked={filterOptions.bookingStatus.includes(opt)}
                        onChange={() => handleFilterChange('bookingStatus', opt)}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <button className="req-btn" type="button" onClick={() => setShowFilters((prev) => !prev)}>
          Filter
        </button>
        <button className="req-btn" type="button" onClick={clearFilters}>
          Clear
        </button>
      </div>

      <Link to="/create-request" className="create-request-button">
        + Create Request
      </Link>
    </div>
  ) : null;

  return (
    <div className="InboxPage">
      <Header />
      <main className="inbox-main">
        <Tabs tabs={tabData} rightContent={inboxHeaderActions}>
          <div className="inbox-shell">
            <aside className="inbox-sidebar">
              <div className="inbox-sidebar-scroll">
                {isApprover ? (
                  <>
                    <div className="sidebar-block">
                      <div className="sidebar-title">Take Action</div>
                      <button
                        type="button"
                        className={`sidebar-link ${activeSection === 'expense' ? 'active' : ''}`}
                        onClick={() => handleSectionChange('expense')}
                      >
                        <span className="sidebar-link-label">
                          <FaFileInvoiceDollar />
                          Expense Requests
                        </span>
                        <span className="sidebar-count">{expenseRequests.length}</span>
                      </button>
                      <button
                        type="button"
                        className={`sidebar-link ${activeSection === 'approval' ? 'active' : ''}`}
                        onClick={() => handleSectionChange('approval')}
                      >
                        <span className="sidebar-link-label">
                          <FaPlaneDeparture />
                          Approval Requests
                        </span>
                        <span className="sidebar-count">{approvalRequests.length}</span>
                      </button>
                    </div>
                    <div className="sidebar-block">
                      <button
                        type="button"
                        className={`sidebar-link ${activeSection === 'notifications' ? 'active' : ''}`}
                        onClick={() => handleSectionChange('notifications')}
                      >
                        <span className="sidebar-link-label">
                          <FaBell />
                          Notifications
                        </span>
                        <span className="sidebar-count">{notifications.length}</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="sidebar-block">
                      <div className="sidebar-title">Tracking</div>
                      <button
                        type="button"
                        className={`sidebar-link ${activeSection === 'tracking' ? 'active' : ''}`}
                        onClick={() => handleSectionChange('tracking')}
                      >
                        <span className="sidebar-link-label">
                          <FaClipboardList />
                          My Submissions
                        </span>
                        <span className="sidebar-count">{trackingRequests.length}</span>
                      </button>
                    </div>
                    <div className="sidebar-block">
                      <button
                        type="button"
                        className={`sidebar-link ${activeSection === 'notifications' ? 'active' : ''}`}
                        onClick={() => handleSectionChange('notifications')}
                      >
                        <span className="sidebar-link-label">
                          <FaBell />
                          Notifications
                        </span>
                        <span className="sidebar-count">{notifications.length}</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </aside>

            <section className="inbox-content">
              <div className="inbox-content-header">
                <div>
                  <p className="inbox-eyebrow">Inbox Workspace</p>
                  <h1>{sectionTitle}</h1>
                </div>
                {activeSection === 'notifications' && !!notifications.length && notifications.some((item) => !item.isRead) && (
                  <button
                    type="button"
                    className="inbox-mark-read-btn"
                    onClick={markAllNotificationsRead}
                  >
                    Mark all read
                  </button>
                )}
              </div>

              <div className="inbox-content-scroll">
                {loading ? (
                  <div className="inbox-empty">Loading inbox...</div>
                ) : activeSection === 'notifications' ? (
                  notifications.length ? (
                    <div className="notification-feed">
                      {notifications.map((notification) => (
                        <div key={notification.id} className={`notification-card ${notification.isRead ? '' : 'unread'}`}>
                          <div className="notification-card-header">
                            <h3>{notification.title}</h3>
                            <span>{formatDate(notification.createdAt)}</span>
                          </div>
                          <p>{notification.message}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="inbox-empty">No notifications available.</div>
                  )
                ) : filteredCurrentList.length ? (
                  <div className="inbox-card-grid">
                    {filteredCurrentList.map((request) => (
                      <article
                        key={request.id}
                        className={`inbox-request-card inbox-request-card-${getStatusSlug(request)}`}
                      >
                        <div className="inbox-request-card-header">
                          <div>
                            <p className="request-chip">
                              {activeSection === 'tracking'
                                ? 'My Request'
                                : request.inboxCategory === 'EXPENSE'
                                  ? 'Expense Request'
                                  : 'Approval Request'}
                            </p>
                            <h3>Request ID: {request.id}</h3>
                          </div>
                          <span className={`status-pill status-${getStatusSlug(request)}`}>
                            {getDisplayStatus(request)}
                          </span>
                        </div>

                        <div className="request-meta-grid">
                          <div>
                            <span>Employee</span>
                            <strong>{request.user?.name || request.email}</strong>
                          </div>
                          <div>
                            <span>Submitted On</span>
                            <strong>{formatDate(request.createdAt)}</strong>
                          </div>
                          <div>
                            <span>Support Needed</span>
                            <strong>{request.supportNeeded || 'NA'}</strong>
                          </div>
                          <div>
                            <span>Current Stage</span>
                            <strong>{getCurrentStageLabel(request.currentStage)}</strong>
                          </div>
                        </div>

                        <div className="request-card-footer">
                          <p>
                            {activeSection === 'tracking'
                              ? 'Your submitted request is being tracked here.'
                              : request.inboxCategory === 'EXPENSE'
                                ? 'Reimbursement request waiting in the approval workflow.'
                                : 'Travel request routed for role-based approval.'}
                          </p>
                          <Link
                            to={`/inbox/${encryptId(request.id)}?section=${activeSection}`}
                            className="detail-link"
                          >
                            Open Details <FaChevronRight />
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="inbox-empty">
                    {isFilterActive || isSearchActive
                      ? 'No requests match the selected search or filters.'
                      : isApprover
                        ? 'There are no actionable requests in this section.'
                        : 'You have not submitted any requests yet.'}
                  </div>
                )}
              </div>
            </section>
          </div>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default Inbox;
